import { useCallback, useEffect, useRef, useState } from "react";
import apiClient from "../../components/utils/apiClient";
import RequestsTable from "../../components/requests/RequestsTable";
import RequestCard from "../../components/requests/RequestCard";
import Toast from "../../components/shared/Toast";
import { getApp, getApps, initializeApp } from "firebase/app";
import {getDatabase, onChildAdded, onChildChanged, onChildRemoved, push, ref} from "firebase/database";
import { firebaseConfig } from "../../components/utils/constants";
import { useTranslation } from "react-i18next";
import useThrottledFetch from "../../hooks/useThrottledFetch";

const NewRequests = ({vendorId,city,fields,setIsActionLoading,showWarning=false}) => {
    const {t} = useTranslation();

    const [requests, setRequests] = useState(null);
    const [workersOptions, setWorkersOptions] = useState(null);
    const [toast, setToast] = useState({show: false,type: null, message:'' });
    const [forceCloseModal, setForceCloseModal] = useState(false);
    const [requestsIds, setRequestsIds] = useState(null);


    const hasInitializedIdsRef = useRef(false);
    useEffect(() => { hasInitializedIdsRef.current = false; }, [vendorId]);

    const requestsIdsRef = useRef(null);
    useEffect(() => { requestsIdsRef.current = requestsIds; }, [requestsIds]);

    let FireBasePath = null;

    if(process.env.REACT_APP_API_URL === 'https://beta-production-api.fanni.site') {
      FireBasePath = 'vendor-requests';
    } else {
      FireBasePath = 'dev/vendor-requests';
    }

    const getRequests = useCallback(async () => {
        try {
          const response = await apiClient.get(`/requests/vendor-requests?skip=0&vendorsId=${vendorId}&cities=${city}&types=${fields?.join(",")}&statuses=pending-vendor-assignment,pending-favorite-worker-approval`);
          if(response?.data) {
            setRequests(response?.data);
            if(!hasInitializedIdsRef.current) {
              hasInitializedIdsRef.current = true;
              setRequestsIds(response?.data?.map(request=>request?.requestPrettyId));
            }
          }
          setIsActionLoading(false);
        } catch(err) {setIsActionLoading(false);}
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [vendorId, city, fields]);

    const getWorkers = async () => {

      try {
        const response = await apiClient.get(`/users?types=worker&vendorsId=${vendorId}`);
        if(response?.data) {
          setWorkersOptions(response?.data?.map(worker=>({label:worker?.name, value:worker?._id})));
        } else {

        }
      } catch(err) {}
    }


    const { enqueue } = useThrottledFetch(getRequests, {
      intervalMs: 5000,
      enabled: !!vendorId,
    });

    useEffect(()=>{
        setIsActionLoading(true);
    },[])

    useEffect(()=>{
      if(vendorId) {
        getRequests();
        getWorkers();
      }
    },[vendorId])


    const handleAddWorkerAction = async(requestId,selectedWorker, business) => {
      const body = {
        "requestId": requestId,
        "workerId": selectedWorker,
        "business": business?._id || ''
        }
        try {
            const response = await apiClient.post(`/requests/vendor-choose-worker`,body);
            if(response && response.status && (response.status === 201 || response.status === 200)) {
                getRequests();
                setToast({show:true,type:'success',message:t('requests.workerAssignedSuccess')});
                setForceCloseModal(true);
                setTimeout(()=>{
                    setForceCloseModal(false);
                },1000);
            } else {
              setToast({show:true,type:'error',message:t('requests.errorOccurred')});

            }
        } catch (error) {
            setToast({show:true,type:'error',message: error?.response?.data?.message || t('requests.errorOccurredRetry')});
        }
    }

    const handleAcceptFavoriteWorkerAction = async (requestId) => {
      try {
            const response = await apiClient.patch(`/requests/${requestId}/favorite-fanni-request/accept`);
            if(response && response.status && (response.status === 201 || response.status === 200)) {
                getRequests();
                setToast({show:true,type:'success',message:t('requests.workerAssignedSuccess')});
                setForceCloseModal(true);
                setTimeout(()=>{
                    setForceCloseModal(false);
                },1000);
            } else {
              setToast({show:true,type:'error',message:t('requests.errorOccurred')});

            }
        } catch (error) {
            setToast({show:true,type:'error',message: error?.response?.data?.message || t('requests.errorOccurredRetry')});
        }
    }

    const handleRejectRequestAction = async (requestId) => {
      try {
            const response = await apiClient.patch(`/requests/${requestId}/favorite-fanni-request/reject`);
            if(response && response.status && (response.status === 201 || response.status === 200)) {
                getRequests();
                setToast({show:true,type:'success',message:t('requests.requestRejectedSuccess')});
                setForceCloseModal(true);
                setTimeout(()=>{
                    setForceCloseModal(false);
                },1000);
            } else {
              setToast({show:true,type:'error',message:t('requests.errorOccurred')});

            }
        } catch (error) {
            setToast({show:true,type:'error',message: error?.response?.data?.message || t('requests.errorOccurredRetry')});
        }
    }

    useEffect(()=>{
        if(!FireBasePath || !requestsIds || !vendorId) return;
        const app = !getApps().length ? initializeApp(firebaseConfig) : getApp();
        const db = getDatabase(app);
        const newRequestsRef = ref(db, `${FireBasePath}/${vendorId}/pending`);
        const unSubscribeNew = onChildAdded(newRequestsRef, (snapshot) => {
            const data = snapshot.key;
            if(data && !requestsIdsRef.current?.includes(data)) {
              enqueue();
            }
        });

        const unSubscribeRemoveNew = onChildRemoved(newRequestsRef, (snapshot) => {
            const data = snapshot.key;
            if(data) {
              enqueue();
            }
        });

        return()=>{
            unSubscribeNew();
            unSubscribeRemoveNew();
        }
    },[FireBasePath,requestsIds,vendorId,enqueue])


    useEffect(()=>{
        if(!FireBasePath || !vendorId) return;
        const app = !getApps().length ? initializeApp(firebaseConfig) : getApp();
        const db = getDatabase(app);
        const newRequestsRef = ref(db, `${FireBasePath}/${vendorId}/pending`);
        const unSubscribeChangedNew = onChildChanged(newRequestsRef, (snapshot) => {
            const data = snapshot.key;
            if(data) {
              enqueue();
            }
        });

        return()=>{
            unSubscribeChangedNew();
        }
    },[FireBasePath,vendorId,enqueue])


    return (
        <>
        {showWarning ? <div className="my-[16px] h-[46px] leading-[46px] bg-infoLight text-info text-[14px] lg:text-[16px] font-[600] rounded-[12px] px-[12px] cairo-font relative">
            <img src="/assets/images/info-circle-info.svg" alt="" className="inline-block me-[8px]" />
            {t('requests.lowBalanceWarning')}
        </div> : ''}
        {requests && <div className="flex flex-wrap gap-[16px] mt-[24px]">
          { Array.isArray(requests) && requests?.length > 0 ? requests?.map((request,index)=>{
            return <RequestCard key={`new-request-${index}`} request={request} newRequest={true} workersOptions={workersOptions} handleAddWorkerAction={handleAddWorkerAction} handleRejectRequestAction={handleRejectRequestAction} handleAcceptFavoriteWorkerAction={handleAcceptFavoriteWorkerAction} forceCloseModal={forceCloseModal} />
          }) : <div className="text-center w-full max-w-[500px] mx-auto pt-[156px] font-[500] text-[24px] text-grey-900">
            <img src="/assets/images/no-requests.svg" alt="" className="w-[188px] h-[188px] mb-[24px] mx-auto" />
            {t('requests.noNewRequests')}
            </div>}
        </div>}
        <Toast toast={toast} setToast={setToast} />
        </>);
}
export default NewRequests;
